import {
  Box,
  Flex,
  Grid,
  GridItem,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { ContractStateDto, FrameworkRole, useGetContractQuery } from "../../../autogen/bff-api";
import { EntityViewSkeleton } from "../../../common/EntityViewSkeleton";
import { EntityUnavailable } from "../../../common/errors/EntityUnavailable";
import { ContractState } from "../../../common/redux/reducers/contractReducer";
import { useQueryParams } from "../../../common/useQueryParams";
import { getCounterpartyAsSeen, requireDataFields } from "../typeguards";
import { getCounterpartyName } from "../view-all/ContractRow";
import { ContractMeta } from "./ContractMeta";
import { DraftContractNotice } from "./DraftContractNotice";
import { FrameworkAgreementLink } from "./FrameworkAgreementLink";
import { FrameworkCeilingExceeded } from "./FrameworkCeilingExceeded";
import { FrameworkContractActions, FrameworkContractStartRefusals } from "./FrameworkContractActions";
import { CallOffsNotShown, SupplierCallOffs } from "./SupplierCallOffs";
import { Details } from "./details/Details";
import { Documents } from "./documents/Documents";
import { Notifications } from "./notifications/Notifications";
import { Sharing } from "./sharing/Sharing";
import { Tasks } from "./tasks/Tasks";
import { ContractDialog } from "./dialog/ContractDialog";
import { contractTabIndex, visibleContractTabs } from "./visibleContractTabs";
import { urls } from "../../../urls";
import { useRecordTitle } from "../../../common/page-title/PageTitle";

export const ContractPage = () => {
  const { contractId } = useParams();
  const [tabIndex, setTabIndex] = useState(0);
  const params = useQueryParams();
  const { t } = useTranslation();
  const {
    data: contract,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useGetContractQuery(contractId ? { contractId } : skipToken);
  useRecordTitle(contract?.title);

  const tabs = contract
    ? visibleContractTabs(contract)
    : [];
  const requestedTab = params.getContractsTab();
  useEffect(() => {
    if (!contract) return;
    setTabIndex(
      contractTabIndex(
        visibleContractTabs(contract),
        requestedTab,
      ),
    );
  }, [contract, requestedTab]);

  if (error) {
    return (
      <EntityUnavailable
        error={error}
        notFoundMessage={t(
          "This contract doesn't exist, or your organization doesn't have access to it.",
        )}
        forbiddenMessage={t(
          "You don't have access to this contract. Ask an administrator in your organization for access.",
        )}
        loadFailedMessage={t("This contract could not be loaded right now.")}
        onRetry={refetch}
        isRetrying={isFetching}
        back={{ to: urls.app.contracts.viewAll, label: t("Back to contracts") }}
      />
    );
  }

  const endDate = contract
    ? requireDataFields(contract as ContractState["contract"]).endDate
    : undefined;

  return (
    <Grid
      width="full"
      gridTemplateColumns={{ base: "minmax(0, 1fr)", md: "3fr 4fr 200px" }}
      gridTemplateRows={{
        base: "auto",
        md: "minmax(50px,auto) auto auto auto minmax(0,1fr)",
      }}
      templateAreas={{
        base: `
              "title"
              "edit"
              "createdMeta"
              "framework"
              "deadline"
              "content"
              `,
        md: `
              "title title edit"
              "createdMeta createdMeta edit"
              "framework framework framework"
              "deadline deadline deadline"
              "content content content"
              `,
      }}
      px="1"
      data-testid="contract-page-grid"
    >
      {isLoading && <EntityViewSkeleton />}
      {contract && contract.id === contractId && (
        <>
          {contract.frameworkAgreementId && (
            <GridItem area="framework" mt="3">
              <Flex align="center" justify="space-between" gap="3" flexWrap="wrap">
                <FrameworkAgreementLink
                  frameworkAgreementId={contract.frameworkAgreementId}
                  role={contract.frameworkRole}
                  mayOpen={contract.frameworkAgreementMayOpen === true}
                />
                {contract.frameworkAgreementMayOpen && contract.canEditEverything && contract.frameworkRole === FrameworkRole.FrameworkSupplierContract && (
                  <FrameworkContractActions frameworkAgreementId={contract.frameworkAgreementId} contractId={contract.id} />
                )}
              </Flex>
              {contract.frameworkAgreementMayOpen && contract.canEditEverything && contract.frameworkRole === FrameworkRole.FrameworkSupplierContract && (
                <FrameworkContractStartRefusals
                  frameworkAgreementId={contract.frameworkAgreementId}
                  contractId={contract.id}
                />
              )}
              {contract.frameworkAgreementMayOpen && contract.frameworkRole === FrameworkRole.CallOff && (
                <FrameworkCeilingExceeded frameworkAgreementId={contract.frameworkAgreementId} />
              )}
              {contract.frameworkRole === FrameworkRole.FrameworkSupplierContract && (
                <Box mt="4">
                  {contract.frameworkAgreementMayOpen ? (
                    <SupplierCallOffs frameworkAgreementId={contract.frameworkAgreementId} contractId={contract.id} />
                  ) : (
                    <CallOffsNotShown />
                  )}
                </Box>
              )}
            </GridItem>
          )}
          <ContractMeta
            id={contract.id}
            title={contract.title}
            counterparty={getCounterpartyAsSeen(contract)}
            status={contract.status ?? undefined}
            isDraft={contract.state === ContractStateDto.Draft}
            expirationDate={endDate?.date ?? null}
            canEdit={contract.canEditEverything}
            canCopy={contract.canCopy}
            canDiscardDraft={contract.canDiscardDraft}
            frameworkAgreementId={contract.frameworkAgreementId}
            template={contract.template ?? null}
          />
          <GridItem
            area={"content"}
            display={"flex"}
            flexDirection={"column"}
            minW="0"
          >
            {contract.state === ContractStateDto.Draft && contract.canEditEverything && (
              <DraftContractNotice
                contractId={contract.id}
                isCallOff={contract.frameworkRole === FrameworkRole.CallOff}
                originProcurementCancelled={contract.originProcurementCancelled ?? false}
                canDiscardDraft={contract.canDiscardDraft ?? false}
              />
            )}
            <Tabs
              isLazy={true}
              mt="25px"
              width={"100%"}
              index={tabIndex}
              onChange={(index) => {
                setTabIndex(index);
                params.setSelectedContractsTab(tabs[index]);
              }}
              variant="line"
              colorScheme="teal"
            >
              <TabList overflowX="auto" overflowY="hidden">
                {contract.canViewDataFields && (
                  <Tab whiteSpace="nowrap">{t("Details")}</Tab>
                )}
                {contract.canViewDocuments && (
                  <Tab whiteSpace="nowrap">{t("Documents")}</Tab>
                )}
                {contract.canViewNotifications && (
                  <Tab whiteSpace="nowrap">{t("Reminders")}</Tab>
                )}
                {contract.canViewTasks && (
                  <Tab whiteSpace="nowrap">{t("Tasks")}</Tab>
                )}
                {contract.canViewParticipants && (
                  <Tab whiteSpace="nowrap">{t("Sharing")}</Tab>
                )}
                <Tab whiteSpace="nowrap">{t("Dialog with counterparty")}</Tab>
              </TabList>
              <TabPanels>
                {contract.canViewDataFields && (
                  <TabPanel p="0" pt="8">
                    <Details data={contract as ContractState["contract"]} />
                  </TabPanel>
                )}
                {contract.canViewDocuments && (
                  <TabPanel p="0" pt="8">
                    <Documents
                      contract={contract}
                      canEdit={contract.canEditEverything}
                    />
                  </TabPanel>
                )}
                {contract.canViewNotifications && (
                  <TabPanel p="0" pt="8">
                    <Notifications data={contract} />
                  </TabPanel>
                )}
                {contract.canViewTasks && (
                  <TabPanel p="0" pt="8">
                    <Tasks contract={contract} />
                  </TabPanel>
                )}
                {contract.canViewParticipants && (
                  <TabPanel p="0" pt="8">
                    <Sharing contract={contract} />
                  </TabPanel>
                )}
                <TabPanel p="0" pt="8">
                  <ContractDialog
                    contractId={contract.id}
                    documents={contract.documentFields?.documents ?? []}
                    canManageContract={contract.canEditEverything}
                    counterpartyName={contract.counterpartyAsSeen ? getCounterpartyName(contract.counterpartyAsSeen) : undefined}
                    showOriginCommunication={
                      contract.dataFields?.originSourcingEvent?.type ===
                      "SourcingEventV2"
                    }
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </GridItem>
        </>
      )}
    </Grid>
  );
};
